import { ChevronDoubleLeftIcon, ChevronDoubleRightIcon } from '@heroicons/react/24/outline'
import { MouseEvent, PointerEvent, ReactNode, useCallback, useEffect, useRef, useState } from 'react'

export type FloaterSide = 'left' | 'right'

interface FloaterTabPosition {
  side: FloaterSide
  top: number
}

interface FloaterTabDefaults {
  /** Edge the tab is docked to before the user moves it. */
  side: FloaterSide
  /** Default vertical placement as a percentage of the viewport height. */
  percent: number
  /** Extra pixels added to the percentage, used to stack sibling tabs. */
  offset?: number
  /** Fixed top pixel position used on mobile so all right-stacked tabs don't overlap. */
  mobileTop?: number
}

const STORAGE_KEY = 'floaterTabPositions'
const TAB_HEIGHT = 48
const EDGE_MARGIN = 8
const DRAG_THRESHOLD = 4

const readPositions = (): Record<string, FloaterTabPosition> => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : {}
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

const writePosition = (key: string, position: FloaterTabPosition | null) => {
  try {
    const positions = readPositions()
    if (position) positions[key] = position
    else delete positions[key]
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(positions))
  } catch {
    // A full or disabled localStorage should never break the floater.
  }
}

const clampTop = (top: number) => {
  const max = Math.max(EDGE_MARGIN, window.innerHeight - TAB_HEIGHT - EDGE_MARGIN)
  return Math.min(Math.max(top, EDGE_MARGIN), max)
}

const mobileRight = (): boolean => typeof window !== 'undefined' && window.innerWidth < 640

const HIDDEN_STORAGE_KEY = 'floaterTabsHidden'

let tabsHidden = (() => {
  try {
    return window.localStorage.getItem(HIDDEN_STORAGE_KEY) === '1'
  } catch {
    return false
  }
})()
const hiddenListeners = new Set<() => void>()

const setTabsHidden = (hidden: boolean) => {
  tabsHidden = hidden
  try {
    if (hidden) window.localStorage.setItem(HIDDEN_STORAGE_KEY, '1')
    else window.localStorage.removeItem(HIDDEN_STORAGE_KEY)
  } catch {
    // Persistence is best-effort; the in-memory toggle still works for this page.
  }
  hiddenListeners.forEach((notify) => notify())
}

/** Whether the floater tabs are minimized down to the edge handles. */
export const useFloaterTabsHidden = () => {
  const [hidden, setHidden] = useState(tabsHidden)
  useEffect(() => {
    const onChange = () => setHidden(tabsHidden)
    hiddenListeners.add(onChange)
    onChange()
    return () => {
      hiddenListeners.delete(onChange)
    }
  }, [])
  return hidden
}

// Live positions of the mounted tabs, so the minimize handles know which edges hold tabs and where.
const mountedTabs = new Map<string, FloaterTabPosition>()
const mountListeners = new Set<() => void>()
const notifyMounts = () => mountListeners.forEach((notify) => notify())

/**
 * Makes a floater tab draggable: up/down anywhere in the viewport, and left/right to dock it on
 * either edge. The panel follows the tab through the returned `side`. Positions are remembered per
 * browser and reset with a right-click on the tab.
 */
export const useFloaterTab = (key: string, defaults: FloaterTabDefaults) => {
  const { side: defaultSide, percent, offset = 0, mobileTop } = defaults

  const getDefaultPosition = useCallback(
    (): FloaterTabPosition => ({ side: defaultSide, top: clampTop((window.innerHeight * percent) / 100 + offset) }),
    [defaultSide, percent, offset],
  )

  const [position, setPosition] = useState<FloaterTabPosition>(() => {
    const defaultPos = getDefaultPosition()
    if (mobileRight()) return { side: 'right', top: mobileTop !== undefined ? clampTop(mobileTop) : defaultPos.top }
    const saved = readPositions()[key]
    if (saved && (saved.side === 'left' || saved.side === 'right') && typeof saved.top === 'number')
      return { side: saved.side, top: clampTop(saved.top) }
    return defaultPos
  })
  const [isDragging, setIsDragging] = useState(false)

  const dragRef = useRef<{ startX: number; startY: number; startTop: number; moved: boolean } | null>(null)
  const suppressClickRef = useRef(false)
  const wasMobileRef = useRef(mobileRight())

  useEffect(() => {
    const onResize = () => {
      const isMobileNow = mobileRight()
      const wasOnMobile = wasMobileRef.current
      wasMobileRef.current = isMobileNow
      setPosition((prev) => {
        const top = clampTop(prev.top)
        if (isMobileNow) return { side: 'right', top: mobileTop !== undefined ? clampTop(mobileTop) : top }
        if (wasOnMobile) {
          const saved = readPositions()[key]
          if (saved && (saved.side === 'left' || saved.side === 'right') && typeof saved.top === 'number')
            return { side: saved.side, top: clampTop(saved.top) }
          return getDefaultPosition()
        }
        return { ...prev, top }
      })
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [key, getDefaultPosition])

  useEffect(() => {
    mountedTabs.set(key, position)
    notifyMounts()
    return () => {
      mountedTabs.delete(key)
      notifyMounts()
    }
  }, [key, position])

  const onPointerDown = (e: PointerEvent<HTMLElement>) => {
    if (e.button !== 0) return
    suppressClickRef.current = false
    dragRef.current = { startX: e.clientX, startY: e.clientY, startTop: position.top, moved: false }
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  const onPointerMove = (e: PointerEvent<HTMLElement>) => {
    const drag = dragRef.current
    if (!drag) return
    const dx = e.clientX - drag.startX
    const dy = e.clientY - drag.startY
    if (!drag.moved && Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return
    drag.moved = true
    setIsDragging(true)
    setPosition({
      side: mobileRight() ? 'right' : e.clientX < window.innerWidth / 2 ? 'left' : 'right',
      top: clampTop(drag.startTop + dy),
    })
  }

  const endDrag = (e: PointerEvent<HTMLElement>) => {
    const drag = dragRef.current
    dragRef.current = null
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId)
    if (!drag?.moved) return
    // The pointer moved, so this gesture was a drag: keep the click from toggling the panel.
    suppressClickRef.current = true
    setIsDragging(false)
    setPosition((prev) => {
      if (!mobileRight()) writePosition(key, prev)
      return prev
    })
  }

  const onContextMenu = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault()
    writePosition(key, null)
    const pos = getDefaultPosition()
    setPosition(mobileRight() ? { side: 'right', top: mobileTop !== undefined ? clampTop(mobileTop) : pos.top } : pos)
  }

  /** Props for the tab button. The panel toggle is passed in so a drag never opens the panel. */
  const tabProps = (onClick: () => void) => ({
    onPointerDown,
    onPointerMove,
    onPointerUp: endDrag,
    onPointerCancel: endDrag,
    onContextMenu,
    onClick: () => {
      if (suppressClickRef.current) {
        suppressClickRef.current = false
        return
      }
      onClick()
    },
    style: { touchAction: 'none' as const, cursor: isDragging ? ('grabbing' as const) : ('grab' as const) },
    title: 'Drag to move this tab. Right-click to reset it.',
  })

  return { side: position.side, top: position.top, isDragging, tabProps }
}

export type FloaterTab = ReturnType<typeof useFloaterTab>

/** Wrapper that pins the tab button to the edge the user dragged it to. */
export const FloaterTabWrapper = ({ floater, children }: { floater: FloaterTab; children: ReactNode }) => {
  const hidden = useFloaterTabsHidden()
  if (hidden) return <></>
  return (
    <div
      className={`floater-tab fixed z-40 ${floater.side === 'right' ? 'floater-panel-right' : ''} ${
        floater.isDragging ? 'select-none' : ''
      }`}
      style={{
        top: floater.top,
        left: floater.side === 'left' ? 0 : undefined,
      }}
    >
      {children}
    </div>
  )
}

const HANDLE_GAP = 30

/**
 * A slim chevron handle on each edge that holds floater tabs: one click minimizes every tab out of
 * the way, another brings them all back. The handle sits just above the topmost tab on its edge and
 * the minimized state is remembered per browser.
 */
export const FloaterTabsMinimizeHandles = () => {
  const hidden = useFloaterTabsHidden()
  const [, setVersion] = useState(0)

  useEffect(() => {
    const onChange = () => setVersion((v) => v + 1)
    mountListeners.add(onChange)
    onChange()
    return () => {
      mountListeners.delete(onChange)
    }
  }, [])

  const sides: FloaterSide[] = ['left', 'right']
  return (
    <>
      {sides.map((side) => {
        const tops = Array.from(mountedTabs.values())
          .filter((tab) => tab.side === side)
          .map((tab) => tab.top)
        if (!tops.length) return null

        const top = Math.max(EDGE_MARGIN, Math.min(...tops) - HANDLE_GAP)
        const pointsOut = (hidden && side === 'left') || (!hidden && side === 'right')
        return (
          <div
            key={side}
            className={`floater-tab fixed z-40 ${side === 'right' ? 'floater-panel-right' : ''}`}
            style={{ top, left: side === 'left' ? 0 : undefined }}
          >
            <button
              className={`px-0.5 py-1 ${floaterTabEdgeClass(
                side,
              )} ring-[1px] ring-shade-blue bg-shade-blue text-white border border-shade-blue hover:bg-white hover:text-shade-blue rounded flex items-center`}
              title={hidden ? 'Show the floater tabs' : 'Minimize the floater tabs'}
              onClick={() => setTabsHidden(!hidden)}
            >
              {pointsOut ? <ChevronDoubleRightIcon className="w-4 h-4" /> : <ChevronDoubleLeftIcon className="w-4 h-4" />}
            </button>
          </div>
        )
      })}
    </>
  )
}

/** Edge-hugging corners for the tab button, so it stays flush with whichever side it sits on. */
export const floaterTabEdgeClass = (side: FloaterSide) =>
  side === 'left' ? 'rounded-l-none border-l-0' : 'rounded-r-none border-r-0'

/** Anchors the open panel to the same edge as its tab. */
export const floaterPanelEdgeClass = (side: FloaterSide) =>
  side === 'left' ? 'floater-panel left-0 rounded-r' : 'floater-panel floater-panel-right rounded-l'
